import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TextField, FormHelperText } from "@mui/material";
import useKeyboard from "@shared/modules/utils/Keyboard/useKeyboard";
import { scrollFieldIntoViewIfHidden } from "./constants";
import EventService from "@shared/services/EventService";

const TextGroupField = forwardRef(({
  label,
  errorMsg,
  value = "",
  onChangeInput,
  containerClass,
  showKeyboard = true,
  virtualKeyboardLayout = null,
  virtualKeyboardLang = "en2",
  groupDetail = [],
  onFieldFocusChange,
  onAllFieldsFilled,
  enableScrollFieldIfHiddenEvent = true,
  onChange = () => {},
  themeName = "alphanumeric",
  ...restProps
}, ref) => {
  const [cardValues, setCardValues] = useState(
    groupDetail.map(f => f.defaultValue || "")
  );
  const [focusIndex, setFocusIndex] = useState(null);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardTimer, setKeyboardTimer] = useState(null);

  // stable refs array
  const inputRefs = useRef([]);
  if (inputRefs.current.length !== groupDetail.length) {
    inputRefs.current = groupDetail.map((_, i) => inputRefs.current[i] || React.createRef());
  }

  const focusRef = useRef(false);

  const { openKeyboard, closeKeyboard } = useKeyboard(value, (updatedValue) => handleVirtualKeyboardChange(updatedValue));

  const totalLength = groupDetail.reduce((a, f) => a + f.maxLength, 0);

  useImperativeHandle(ref, () => ({
    focus: (index = 0) => {
      if (
        index >= 0 &&
        index < groupDetail.length &&
        inputRefs.current[index].current
      ) {
        handleFocus(index);
      }
    //   const fieldConfig = groupDetail[0];

    // // 🚫 If readonly → skip any change
    // if (fieldConfig?.readOnly) {
    //   let val=cardValues
    //   val[0] = fieldConfig.defaultValue
    //   setCardValues(val)
    //   if(value.length< fieldConfig.defaultValue.length){
    //     onChangeInput(fieldConfig.defaultValue)
    //   }
    //   else{
    //     let comval = fieldConfig.defaultValue + value.slice(fieldConfig.maxLength)
    //   }
    // }
    }
  }));

  useEffect(() => {
    let eventInput = EventService.subscribe("keypad.blur", () => {
      setKeyboardVisible(false); // Hide keyboard on blur if necessary
    });
    let eventInput2 = EventService.subscribe("keypad.focus", () => {
      setKeyboardVisible(true); // Show keyboard on focus
    });

    // const fieldConfig = groupDetail[0];

    // // 🚫 If readonly → skip any change
    // if (fieldConfig?.readOnly) {
    //   let val=cardValues
    //   val[0] = fieldConfig.defaultValue
    //   setCardValues(val)
    //   if(value.length< fieldConfig.defaultValue.length){
    //     onChangeInput(fieldConfig.defaultValue)
    //   }
    //   else{
    //     let comval = fieldConfig.defaultValue + value.slice(fieldConfig.maxLength)
    //     onChangeInput(comval)
    //   }
    // }

    let scrollEvent = enableScrollFieldIfHiddenEvent
      ? EventService.subscribe("keyboard.show", () => {
          if (focusRef.current) {
              scrollFieldIntoViewIfHidden(inputRefs.current["1"]);
          }
      })
      : {};

    return () => {
      eventInput.remove();
      eventInput2.remove();
      if (scrollEvent.remove) scrollEvent.remove();
    };
  }, []);
  // Sync `value` into cardValues
  useEffect(() => {
    if (value) {
      const splitValues = [];
      let start = 0;
      groupDetail.forEach(f => {
        splitValues.push(value.slice(start, start + f.maxLength));
        start += f.maxLength;
      });
      setCardValues(splitValues);
    }
  }, [value, groupDetail]);

  const handleChange = (e, index) => {
    const sanitizedValue = e ? e.target.value.replace(/\D/g, "") : groupDetail[index].defaultValue; // allow only digits
    const maxLen = groupDetail[index].maxLength;

    if (sanitizedValue.length <= maxLen) {
      const updatedCardValues = [...cardValues];
      updatedCardValues[index] = sanitizedValue;

      const combinedValue = updatedCardValues.join("");
      if (combinedValue.length <= totalLength) {
        setCardValues(updatedCardValues);
        onChangeInput(combinedValue);
        onChange(combinedValue);

        // auto-move focus when field is filled
        if (sanitizedValue.length === maxLen && index < groupDetail.length - 1) {
          handleFocus(index + 1);
        }
      }
    }
  };

  const handleVirtualKeyboardChange = (combinedValue) => {
    // const fieldConfig = groupDetail[0];

    // 🚫 If readonly → skip any change
    // if (2 > combinedValue.length) {
    //   return
    // }
    if (combinedValue.length <= totalLength) {
      const splitValues = [];
      let start = 0;
      groupDetail.forEach(f => {
        splitValues.push(combinedValue.slice(start, start + f.maxLength));
        start += f.maxLength;
      });
      setCardValues(splitValues);

      const activeInd = splitValues.findIndex(
        (val, i) => val.length < groupDetail[i].maxLength
      );
      handleFocus(activeInd >= 0 ? activeInd : null);

      onChangeInput(combinedValue);
    }
  };

  const handleFocus = (index) => {
    // const fieldConfig = groupDetail[index];

    // // 🚫 If readonly → skip any change
    // if (fieldConfig?.readOnly) {
    //   index=index+1
    // }
    setFocusIndex(index);
    focusRef.current = true;

    if (cardValues[index]?.trim() === "" && typeof onFieldFocusChange === "function") {
      onFieldFocusChange(index);
    }

    if (showKeyboard && !keyboardVisible) {
      openKeyboard({
        layoutName: virtualKeyboardLayout,
        virtualKeyboardLang,
        themeName: themeName,
      });
      setKeyboardVisible(true);
    }

    if (inputRefs.current[index]) {
      inputRefs.current[index].current?.focus();
    }
  };

  const handleBlur = (index) => {
    setFocusIndex(null);
    focusRef.current = false;

    if (keyboardTimer) clearTimeout(keyboardTimer);

    const allFilled = cardValues.every(
      (val, i) => val.length === groupDetail[i].maxLength
    );

    if (allFilled && typeof onAllFieldsFilled === "function") {
      onAllFieldsFilled(cardValues.join(""));
    }

    setKeyboardTimer(setTimeout(() => {
      if (focusIndex === null) {
        closeKeyboard();
        setKeyboardVisible(false);
      }
    }, 300));
  };

  const gridTemplate = groupDetail.map(f => `${f.maxLength}fr`).join(" ");

  return (
    <div className={`input-field-sec card-fields ${errorMsg ? "error-field" : ""} ${containerClass || ""} ${label && "with-label"}`}
      style={{
        display: "grid",
        gridTemplateColumns: gridTemplate,
        gap: "12px",
        alignItems: "center"
      }}
    >
      {cardValues.map((fieldValue, index) => {
        const fieldConfig = groupDetail[index];
        return (
          <div key={index} style={{ display: "flex", alignItems: "center", height: "120px" }}>
            <TextField
              ref={inputRefs.current[index]}
              value={fieldValue}
              onFocus={() => handleFocus(index)}
              autoFocus={false}
              focused={fieldValue.length === fieldConfig?.maxLength || index === focusIndex}
              onBlur={() => handleBlur(index)}
              inputProps={{
                maxLength: fieldConfig?.maxLength,
                minLength: fieldConfig?.minLength,
                readOnly: fieldConfig?.readOnly,
                style: { textAlign: "center" },
              }}
              variant="outlined"
              {...restProps}
              onChange={(e) => handleChange(e, index)}
            />
            {/* render separator only between fields */}
            {index < groupDetail.length - 1 && fieldConfig?.separator && (
              <span style={{ margin: "0 8px", fontSize: "1.5rem", color: "#aaa" }}>
                {fieldConfig.separator}
              </span>
            )}
          </div>
        );
      })}
      {label && <FormHelperText>{label}</FormHelperText>}
      {errorMsg && <span className="helperTextError">{errorMsg}</span>}
    </div>
  );
});

export default TextGroupField;
